import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { TIME_SLOTS } from "@/data/content";

export const STATUS_LABELS = {
  request_received: "Request Received",
  under_review: "Under Review",
  alternative_proposed: "Alternative Proposed",
  awaiting_customer_response: "Awaiting Customer Response",
  awaiting_payment: "Awaiting Payment",
  confirmed_paid: "Confirmed — Paid",
  declined: "Declined",
  cancelled: "Cancelled",
  payment_failed_expired: "Payment Failed or Expired",
};

const inputCls = "w-full rounded-lg border border-subtle bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-blue";
const btn = "rounded-full px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors disabled:opacity-50";
const OPEN = ["request_received", "under_review"];

function History({ items }) {
  if (!items?.length) return null;
  return (
    <ol className="mt-4 space-y-2 border-l border-subtle pl-4" data-testid="admin-status-history">
      {items.map((h, i) => (
        <li key={i} className="text-xs leading-relaxed text-body">
          <span className="font-medium text-ink">{h.label || STATUS_LABELS[h.status] || h.status}</span>
          <span className="text-faint"> · {new Date(h.at).toLocaleString("en-GB")} · {h.by}</span>
          {h.note && <span className="block text-faint">{h.note}</span>}
        </li>
      ))}
    </ol>
  );
}

function ProposeForm({ onSubmit, busy }) {
  const [f, setF] = useState({ date: "", time_slot: "morning", message: "" });
  return (
    <div className="mt-4 grid gap-3 rounded-xl border border-subtle bg-warmcard p-4 sm:grid-cols-2" data-testid="admin-propose-form">
      <input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="admin-propose-date" />
      <select className={inputCls} value={f.time_slot} onChange={(e) => setF({ ...f, time_slot: e.target.value })} data-testid="admin-propose-slot">
        {TIME_SLOTS.map((t) => <option key={t.value} value={t.value}>{t.label} ({t.hint})</option>)}
      </select>
      <textarea rows={2} className={`${inputCls} sm:col-span-2`} placeholder="Optional personal message to the customer" value={f.message}
        onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="admin-propose-message" />
      <button disabled={busy || !f.date} onClick={() => onSubmit(f)} data-testid="admin-propose-send" className={`${btn} bg-blue text-canvas hover:bg-blue-hover sm:col-span-2`}>
        Send alternative to customer
      </button>
    </div>
  );
}

export function RequestActions({ booking: b, onUpdated, flash }) {
  const [busy, setBusy] = useState(false);
  const [proposing, setProposing] = useState(false);
  const run = async (path, body = {}, done = "Updated") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/${path}`, body);
      onUpdated(data);
      setProposing(false);
      flash(done);
    } catch (err) {
      if (err.response?.status === 409 && path === "approve" && window.confirm(`${apiError(err)}\n\nAccept this request anyway?`)) {
        setBusy(false);
        return run(path, { force: true }, done);
      }
      window.alert(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const ask = (text) => window.prompt(text, "");
  const s = b.status;
  const legacy = !STATUS_LABELS[s];

  return (
    <div className="mt-7 rounded-2xl border border-subtle p-5" data-testid="admin-request-actions">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Booking status</p>
      <p className="mt-1 font-serif text-2xl text-ink" data-testid="admin-request-status">{STATUS_LABELS[s] || s}</p>
      {b.requested_date && (
        <p className="mt-2 text-sm text-body" data-testid="admin-requested-slot">
          Requested: {b.requested_date} · {b.requested_time_slot_label}
          {b.date !== b.requested_date || b.time_slot !== b.requested_time_slot ? ` → Now: ${b.date} · ${b.time_slot_label}` : ""}
        </p>
      )}
      {b.proposal && (
        <p className="mt-1 text-sm text-body" data-testid="admin-proposal">
          Proposed: {b.proposal.date} · {b.proposal.time_slot_label} — {b.proposal.response ? `customer ${b.proposal.response}` : "awaiting reply"}
        </p>
      )}
      {OPEN.includes(s) && <p className="mt-3 text-xs text-faint">Check that a suitable independent cleaner is available before accepting.</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        {s === "request_received" && (
          <button disabled={busy} onClick={() => run("review", {}, "Marked under review")} data-testid="admin-action-review" className={`${btn} border border-subtle text-ink hover:border-blue`}>Mark under review</button>
        )}
        {OPEN.includes(s) && (
          <button disabled={busy} onClick={() => window.confirm(`Accept ${b.date} · ${b.time_slot_label} and email the customer a payment link for £${b.total_price}?`) && run("approve", {}, "Accepted — payment link sent")}
            data-testid="admin-action-accept" className={`${btn} bg-blue text-canvas hover:bg-blue-hover`}>Accept request</button>
        )}
        {(OPEN.includes(s) || s === "awaiting_customer_response") && (
          <button disabled={busy} onClick={() => setProposing((v) => !v)} data-testid="admin-action-propose" className={`${btn} border border-blue text-blue hover:bg-blue hover:text-canvas`}>Propose alternative</button>
        )}
        {(OPEN.includes(s) || ["awaiting_customer_response", "payment_failed_expired"].includes(s)) && (
          <button disabled={busy} onClick={() => { const m = ask("Optional message to the customer (leave blank for none):"); if (m !== null) run("decline", { message: m }, "Request declined"); }}
            data-testid="admin-action-decline" className={`${btn} border border-red-300 text-red-700 hover:bg-red-50`}>Decline</button>
        )}
        {["awaiting_payment", "payment_failed_expired"].includes(s) && (
          <button disabled={busy} onClick={() => run("resend-payment", {}, "Payment link re-sent")} data-testid="admin-action-resend" className={`${btn} border border-subtle text-ink hover:border-blue`}>Resend payment link</button>
        )}
        {[...OPEN, "awaiting_customer_response", "awaiting_payment", "payment_failed_expired"].includes(s) && (
          <button disabled={busy} onClick={() => { const m = ask("Reason for cancelling (internal, optional):"); if (m !== null) run("cancel", { message: m }, "Booking cancelled"); }}
            data-testid="admin-action-cancel" className={`${btn} border border-subtle text-faint hover:border-red-300 hover:text-red-700`}>Cancel</button>
        )}
      </div>
      {proposing && <ProposeForm busy={busy} onSubmit={(f) => run("propose", f, "Alternative sent to customer")} />}
      {legacy && <p className="mt-3 text-xs text-faint">This booking was made before booking requests were introduced.</p>}
      <History items={b.status_history} />
    </div>
  );
}
